/**
 * Image URL collection and Dropbox transform for v2 mapping.
 *
 * Pure functions only. Reads from a SourceRow using an ImageConfig and emits
 * a list of image URLs ready to drop into Shopify's "Image Src" column.
 *
 * See docs/V2-PLAN.md §2 "Image rows".
 */

import { resolveTemplate } from "@/modules/_shared/templateValue";
import type { TemplateValue } from "@/modules/_shared/templateValue";
import { defaultHelpers } from "./helpers";
import type {
  HelperRegistry,
  ImageConfig,
  ImageSlot,
  ShopifyRow,
  SourceRow,
  SourceCell,
} from "./types";

/**
 * Detect source image-URL columns from a sheet's headers.
 *
 * Matches headers that look like image-URL sources (image / img / photo /
 * picture), excluding Shopify output columns that merely contain "Image".
 * Numbered headers sort numerically, so "Image link 2" stays before
 * "Image link 10"; non-numbered matches stay in first-seen order after them.
 */
const IMAGE_HEADER_RE = /\b(images?|imgs?|photos?|pictures?)\b/i;
const COMPACT_IMG_HEADER_RE = /^imgs?\s*\d+$/i;
const EXCLUDED_IMAGE_HEADERS = new Set([
  "image alt text",
  "image position",
  "image src",
  "variant image",
]);
/** Headers ABOUT an image rather than its URL ("Image 2 Alt", "Image count"). */
const IMAGE_META_HEADER_RE =
  /\b(alt|alt\s*text|alternate|position|caption|description|desc|count|qty|quantity)\b/i;

export function detectImageColumns(
  headers: readonly string[] | null | undefined,
): string[] {
  if (!Array.isArray(headers)) return [];

  const seen = new Set<string>();
  const matched: Array<{ header: string; ordinal: number | null; index: number }> = [];

  for (const raw of headers) {
    const header = String(raw ?? "").trim();
    if (header.length === 0 || seen.has(header)) continue;

    const lower = header.toLowerCase();
    if (EXCLUDED_IMAGE_HEADERS.has(lower)) continue;
    if (IMAGE_META_HEADER_RE.test(header)) continue;
    if (!IMAGE_HEADER_RE.test(header) && !COMPACT_IMG_HEADER_RE.test(header)) {
      continue;
    }

    seen.add(header);
    matched.push({
      header,
      ordinal: trailingNumber(header),
      index: matched.length,
    });
  }

  matched.sort((a, b) => {
    if (a.ordinal !== null && b.ordinal !== null) return a.ordinal - b.ordinal;
    if (a.ordinal !== null) return -1;
    if (b.ordinal !== null) return 1;
    return a.index - b.index;
  });

  return matched.map((entry) => entry.header);
}

function trailingNumber(value: string): number | null {
  const match = value.match(/(\d+)\s*$/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Rewrite a Dropbox share URL into a direct-download URL that Shopify can fetch.
 *
 * - `https://www.dropbox.com/s/abc/foo.jpg?dl=0`
 *     → `https://dl.dropboxusercontent.com/s/abc/foo.jpg`
 * - Any `?dl=N` query param is stripped.
 * - Other query params are preserved.
 * - Non-Dropbox URLs pass through unchanged.
 * - Empty / whitespace-only input returns "".
 */
export function transformDropbox(url: string): string {
  if (url == null) return "";
  const trimmed = String(url).trim();
  if (trimmed.length === 0) return "";

  // Detect Dropbox hostname. We're permissive: any host containing "dropbox.com"
  // (covers www.dropbox.com, dropbox.com). Already-direct URLs
  // (dl.dropboxusercontent.com) pass through unchanged.
  const lower = trimmed.toLowerCase();
  if (lower.includes("dl.dropboxusercontent.com")) return trimmed;
  if (!lower.includes("dropbox.com")) return trimmed;

  // Swap the host. Be careful to only replace the host portion.
  let rewritten = trimmed.replace(
    /^(https?:\/\/)([^/]*\.)?dropbox\.com/i,
    "$1dl.dropboxusercontent.com",
  );

  // Strip the `dl` query parameter (`?dl=0`, `&dl=1`, etc.).
  // Split into path-and-query to operate safely on the query string only.
  const queryIdx = rewritten.indexOf("?");
  if (queryIdx !== -1) {
    const path = rewritten.slice(0, queryIdx);
    const query = rewritten.slice(queryIdx + 1);
    const kept = query
      .split("&")
      .filter((pair) => {
        const eq = pair.indexOf("=");
        const key = eq === -1 ? pair : pair.slice(0, eq);
        return key.toLowerCase() !== "dl";
      })
      .join("&");
    rewritten = kept.length > 0 ? `${path}?${kept}` : path;
  }

  return rewritten;
}

/**
 * Find the alt-text column that belongs to an image column: same number, an
 * "alt" word, e.g. "Image 2" ↔ "Image 2 Alt" / "Alt Text 2" / "Image Alt 2".
 * An un-numbered image column pairs with an un-numbered alt column.
 */
function pairedAltColumn(
  headers: readonly string[],
  imageHeader: string,
): string | undefined {
  const n = trailingNumber(imageHeader.replace(/\D+$/, "")) ?? trailingNumber(imageHeader);
  return headers.find((raw) => {
    const h = String(raw ?? "").trim();
    if (!/\balt\b/i.test(h)) return false;
    const m = h.match(/(\d+)/);
    const hn = m ? Number(m[1]) : null;
    return hn === n;
  });
}

/** Detected image columns as slots, each paired with its alt column when one exists. */
export function detectImageSlots(
  headers: readonly string[] | null | undefined,
): ImageSlot[] {
  if (!Array.isArray(headers)) return [];
  return detectImageColumns(headers).map((col) => {
    const alt = pairedAltColumn(headers, col);
    return {
      src: fieldValue(col),
      ...(alt ? { alt: fieldValue(alt) } : {}),
    };
  });
}

/** A template holding just one source column. */
export function fieldValue(column: string): TemplateValue {
  return [{ t: "field", name: column, origin: "source" }];
}

/** The source column a slot reads, when its src is exactly one source field. */
export function slotColumn(slot: ImageSlot): string | undefined {
  const src = slot.src;
  if (typeof src === "string") {
    const m = src.trim().match(/^\{\{\s*([^|}]+?)\s*\}\}$/);
    return m && !m[1].toLowerCase().startsWith("out:") ? m[1] : undefined;
  }
  if (Array.isArray(src) && src.length === 1) {
    const seg = src[0];
    if (seg && seg.t === "field" && seg.origin === "source" && !seg.pipes?.length) {
      return seg.name;
    }
  }
  return undefined;
}

/**
 * The ordered image slots a config describes: `slots` when present, otherwise
 * one slot per legacy `sourceColumns` entry (no per-image alt).
 */
export function imageSlotsOf(cfg: ImageConfig | undefined): ImageSlot[] {
  if (!cfg) return [];
  if (Array.isArray(cfg.slots)) return cfg.slots;
  return (cfg.sourceColumns ?? []).map((col) => ({ src: fieldValue(col) }));
}

/** Source headers referenced by slots that are a single column (keeps `sourceColumns` in sync). */
export function slotSourceColumns(slots: readonly ImageSlot[]): string[] {
  const out: string[] = [];
  for (const slot of slots) {
    const col = slotColumn(slot);
    if (col && !out.includes(col)) out.push(col);
  }
  return out;
}

/**
 * Split a cell that holds several image URLs. New lines always separate;
 * commas / semicolons / pipes / spaces only when the next piece starts with
 * http(s):// — so a single URL with commas (Cloudinary "w_400,h_300") stays whole.
 */
export function splitImageUrls(value: string): string[] {
  return String(value ?? "")
    .split(/[\r\n]+|[\s,;|]+(?=https?:\/\/)/i)
    .map((part) => part.trim().replace(/^[,;|]+|[,;|]+$/g, "").trim())
    .filter((part) => part.length > 0);
}

export type ImageEntry = {
  src: string;
  /** Per-image alt (slot alt, else the config default); "" when neither resolves. */
  alt: string;
};

/**
 * Resolve every image for one source row, in position order. `output` (the
 * resolved product row) lets alt/src templates read workbook fields like Title.
 * Pure; never throws.
 */
export function collectImageEntries(
  row: SourceRow,
  cfg: ImageConfig | undefined,
  ctx: { helpers?: HelperRegistry; output?: ShopifyRow } = {},
): ImageEntry[] {
  if (!cfg) return [];
  const resolveCtx = { row, helpers: ctx.helpers ?? defaultHelpers, output: ctx.output };
  const defaultAlt =
    cfg.alt !== undefined ? resolveTemplate(cfg.alt, resolveCtx).trim() : "";
  const out: ImageEntry[] = [];
  for (const slot of imageSlotsOf(cfg)) {
    const col = slotColumn(slot);
    // A plain column reads the raw cell (Dates → ISO) exactly like the legacy path.
    const raw = col !== undefined ? cellToString(row[col]) : resolveTemplate(slot.src, resolveCtx);
    const slotAlt =
      slot.alt !== undefined ? resolveTemplate(slot.alt, resolveCtx).trim() : "";
    for (const piece of splitImageUrls(raw)) {
      const url = cfg.transform === "dropbox-direct" ? transformDropbox(piece) : piece;
      if (url.length === 0) continue;
      out.push({ src: url, alt: slotAlt || defaultAlt });
    }
  }
  return out;
}

/**
 * Read image URLs from a row according to an ImageConfig.
 *
 * Iterates the configured image columns / slots in order, skips empty cells,
 * splits cells holding several URLs, optionally applies the Dropbox
 * direct-download transform.
 */
export function collectImages(row: SourceRow, cfg: ImageConfig): string[] {
  return collectImageEntries(row, cfg).map((entry) => entry.src);
}

function cellToString(cell: SourceCell | undefined): string {
  if (cell == null) return "";
  if (cell instanceof Date) return cell.toISOString();
  return String(cell);
}
